import { getReferenceString, MedplumClient } from '@medplum/core';
import { CodeableConcept, Coding, Practitioner, Extension, Reference, Resource } from '@medplum/fhirtypes';
import { v4 as uuidv4 } from 'uuid';
import { FORCE_RESOURCE_UPDATE_EXTENSION } from './extensions';

// Normalize to handle unicode on text
export function normalizeTextContent(text: string): string {
  return text.toLowerCase().normalize('NFKC').trim();
}

export function isReferenceOf<T extends string>(ref: { reference?: string } | undefined, resourceType: T): boolean {
  return !!ref?.reference?.startsWith(`${resourceType}/`);
}

/**
 * Checks if two references refer to the same resource
 * @param a - First reference
 * @param b - Second reference
 * @returns true if the references refer to the same resource
 */
export function referencesEqual(a?: Reference, b?: Reference): boolean {
  if (!a || !b) {
    return false;
  }

  return getReferenceString(a) === getReferenceString(b);
}

/**
 * Medplum ignores update calls if the resource has not changed.
 * This function forces an update by changing the value of a extension.
 */
export async function forceUpdateResource(medplum: MedplumClient, resource: Resource): Promise<Resource> {
  const metaExtension =
    resource.meta?.extension?.filter((extension) => extension.url !== FORCE_RESOURCE_UPDATE_EXTENSION) ?? [];

  return await medplum.updateResource({
    ...resource,
    meta: {
      ...resource.meta,
      extension: [
        ...metaExtension,
        {
          url: FORCE_RESOURCE_UPDATE_EXTENSION,
          valueString: uuidv4(),
        },
      ],
    },
  });
}

export function hasCode(code: string): (concept?: { coding?: Coding[] }) => boolean {
  return function (concept?: { coding?: Coding[] }): boolean {
    return concept?.coding?.some((c) => c.code === code) ?? false;
  };
}

export function getUUID() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2, 15);
}

/**
 * Reads a reference and returns undefined if the resource is not found.
 * @param medplum - Medplum client instance
 * @param reference - Reference to the resource
 * @returns Promise<T | undefined> - The resource or undefined if not found
 */
export async function safeReadReference<T extends Resource>(medplum: MedplumClient, reference: Reference<T>) {
  try {
    const resource = await medplum.readReference<T>(reference);
    return resource;
  } catch (_: any) {
    return undefined;
  }
}

/**
 * Gets the code from a CodeableConcept.
 */
export function getCodeableConceptCode(codeableConcept?: CodeableConcept) {
  if (!codeableConcept) {
    return;
  }

  return codeableConcept.coding?.[0]?.code;
}

/**
 * FHIR reference regex: "ResourceType/id" where id allows alphanumeric, hyphens, and dots per FHIR spec.
 */
const FHIR_REFERENCE_REGEX = /^[A-Z][a-zA-Z]+\/[A-Za-z0-9\-.]+$/;

/**
 * Checks if a string is a valid FHIR resource reference (e.g., "Patient/uuid").
 */
export function isResourceReference(ref: string): boolean {
  return FHIR_REFERENCE_REGEX.test(ref);
}

/**
 * Clears blank extension values from an array of extensions.
 * @param extensions - Array of extensions
 * @returns Array of extensions with blank values removed
 */
export function clearBlankExtensionValues(extensions: Extension[]) {
  for (const extension of extensions) {
    for (const [key, value] of Object.entries(extension)) {
      if (Array.isArray(value) && value.length > 0) {
        extension[key as keyof Extension] = clearBlankExtensionValues(value) as never;
        continue;
      }

      if (!value) {
        delete extension[key as keyof Extension];
      }
    }
  }

  return extensions;
}

/**
 * Gets the list of emails from a Practitioner.
 */
export function getPractitionerEmailList(practitioner: Practitioner) {
  const emailSet = practitioner.telecom?.reduce((emailSet, telecom) => {
    if (telecom.system === 'email' && telecom.value) {
      emailSet.add(telecom.value);
    }
    return emailSet;
  }, new Set<string>());

  return emailSet ? Array.from(emailSet) : [];
}

export function trimStringValues<T = any>(input: T): T {
  if (typeof input === 'string') {
    return input.trim() as T;
  }

  if (Array.isArray(input)) {
    return input.map(trimStringValues) as T;
  }

  if (input instanceof Date) {
    return input;
  }

  if (typeof input === 'object' && input !== null) {
    return Object.fromEntries(Object.entries(input).map(([key, value]) => [key, trimStringValues(value)])) as T;
  }

  return input;
}
